import React from 'react';
import { motion } from 'framer-motion';
import { FaClipboardCheck, FaTools, FaGraduationCap, FaRocket, FaChartLine } from 'react-icons/fa';

const timeline = [
  {
    id: "strategy",
    day: "Day 1-2",
    title: "STRATEGY",
    icon: <FaClipboardCheck className="text-4xl" />,
    description: "We audit your current intake, identify the leaks, and design your custom system.",
    color: "emerald"
  },
  {
    id: "build",
    day: "Day 3-5",
    title: "BUILD",
    icon: <FaTools className="text-4xl" />,
    description: "We create your forms, configure your AI, set up automations, and integrate everything.",
    color: "emerald"
  },
  {
    id: "train",
    day: "Day 6",
    title: "TRAIN",
    icon: <FaGraduationCap className="text-4xl" />,
    description: "Your team gets a 60-minute training session. Everyone knows exactly what to do.",
    color: "emerald"
  },
  {
    id: "launch",
    day: "Day 7",
    title: "LAUNCH",
    icon: <FaRocket className="text-4xl" />,
    description: "Your AI receptionist goes live. Your forms go live. You start capturing leads 24/7.",
    color: "emerald"
  },
  {
    id: "optimize",
    day: "Day 8+",
    title: "OPTIMIZE",
    icon: <FaChartLine className="text-4xl" />,
    description: "We monitor, adjust, and improve. Your system gets better every month.",
    color: "emerald"
  }
];

const HowItWorks = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 relative" id="how-it-works">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Live in <span className="gradient-text">7-14 Days.</span> Not 7 Months.
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            From strategy to launch, we make it fast and painless
          </p>
        </motion.div>

        <div className="relative">
          {/* Vertical line for desktop */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-emerald-500 to-emerald-600"></div>

          <div className="space-y-12">
            {timeline.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                data-testid={`timeline-step-${index}`}
              >
                {/* Content */}
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                  <div className="bg-slate-800/70 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover-lift">
                    <div className="text-emerald-400 font-bold text-sm mb-2">{step.day}</div>
                    <h3 className="text-2xl font-bold mb-3 text-white">{step.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{step.description}</p>
                  </div>
                </div>

                {/* Icon circle */}
                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full items-center justify-center text-white shadow-lg shadow-emerald-500/50 z-10">
                  {step.icon}
                </div>

                {/* Spacer for opposite side */}
                <div className="hidden md:block w-5/12"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border border-emerald-500/30 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 text-white">
              Your AI Receptionist Answers Its First Call Within 48 Hours of Launch
            </h3>
            <p className="text-lg text-gray-300">
              Your forms go live, your automations start running, and you begin capturing leads you were previously losing.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
